/* eslint-disable max-len */
import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Prop,
  State,
} from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';

interface UiTextareaChangeEvent {
  value: string;
}

/**
 * The <textarea> component defines a multi-line text input control. It is often used in a form, to collect user inputs like comments or reviews. A text area can hold an unlimited number of characters. The size of a text area is specified by the <rows> attribute.
 * The name attribute is needed to reference the form data after the form is submitted (if you omit the name attribute, no data from the text area will be submitted).
 * The fieldId attribute is needed to associate the text area with a label.
 */
@Component({
  tag: 'ui-textarea',
  styleUrl: 'ui-textarea.scss',
  shadow: false,
})
export class UiTextarea {
  @Element() el: HTMLElement;

  /**
   * Contains the message that shows the remaining characters available in the textarea.
   */
  private lengthCounterMessage: string;

  /**
   * Specifies a short hint that describes the expected value of an `<textarea>` element.
   */
  @Prop() placeholder: string;

  /**
   * Specifies how many rows the component has, which alters its size vertically.
   */
  @Prop() rows = 5;

  /**
   * Allows the button to have two modes: enabled or disabled until a certain requirement is met.
   */
  @Prop() disabled: boolean;

  /**
   * Specifies the name the `<textarea>` component.
   */
  @Prop() name: string;

  @Prop({ mutable: true }) fieldId: string;

  /**
   * Defines the select component's title
   */
  @Prop() label: string;

  /**
   * Gives the user a clue for what type of information or action is required of them.
   */
  @Prop() hint: string;

  /**
   * Message that appears below the input element, informing the user of something.
   */
  @Prop() message: string;

  /**
   * String that represents the user's input into the text area
   */
  @Prop({ mutable: true }) value = '';

  /**
   * When set to true, requires to user to input something into the text area.
   */
  @Prop() required: boolean;

  /**
   * Checks whether the user's input was invalid. Changes the component's appearance when true.
   */
  @Prop() error: boolean;

  /**
   * Checks whether the user's input was valid. Changes the component's appearance when true.
   */
  @Prop() success: boolean;

  /**
   * Limits the number of characters allowed in the textarea
   */
  @Prop() maxlength: number;

  /**
   * Counter of the number of characters written in the text area.
   */
  @State() lengthCounter: number;

  /**
   * Is triggered when the user changes something on the `<textarea>` element.
   */
  @Event({ eventName: 'uiInputChange' })
  change: EventEmitter<UiTextareaChangeEvent>;

  textarea: HTMLTextAreaElement;

  componentWillLoad() {
    this.handleLengthCounter();
  }

  componentDidLoad() {
    this.fieldId = this.el.id || this.name;
  }

  onChange() {
    if (this.value !== this.textarea.value) {
      this.value = this.textarea.value;
      this.change.emit({ value: this.textarea.value });
      this.handleLengthCounter();
    }
  }

  onKeyUp() {
    this.onChange();
  }

  handleLengthCounter() {
    if (this.maxlength > 0) {
      this.lengthCounter = this.maxlength - this.value.length;
      this.lengthCounterMessage = `${this.lengthCounter} ${window.ui.services.i18n.translate('remainingCharacters') || 'Zeichen verbleibend'}`;
    }
  }

  render() {
    return (
      <div
        class={{
          textarea: true,
          'textarea--error': this.error,
          'textarea--disabled': this.disabled,
          'textarea--success': this.success,
        }}
      >
        {this.label && (
          <label class="textarea__label" htmlFor={this.fieldId}>
            {this.label}
            {this.required && <span class="required">*</span>}
          </label>
        )}
        <textarea
          id={this.fieldId}
          onChange={this.onChange.bind(this)}
          onKeyUp={this.onKeyUp.bind(this)}
          placeholder={this.placeholder}
          required={this.required}
          name={this.name}
          ref={(textarea) => {
            this.textarea = textarea;
          }}
          disabled={this.disabled}
          value={this.value}
          rows={this.rows}
          maxLength={this.maxlength ? this.maxlength : undefined}
        />
        {this.lengthCounterMessage && (
          <div class="textarea__lengthCounterMessage">
            {this.lengthCounterMessage}
          </div>
        )}
        {this.hint && <div class="textarea__hint">{this.hint}</div>}
        {this.message && <div class="textarea__message">{this.message}</div>}
      </div>
    );
  }
}

versionComponent(UiTextarea);
